import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import numbro from 'numbro';
import { shallow } from 'zustand/shallow';

import { SpacePerformanceReportSummary } from '../../api/Rest/space';
import { FolderBreadcrumbText } from '../../components/Molecules';
import { ClubSelectFolderPopup } from '../../components/Popups';
import {
  ReportSummaryPopup,
  SummaryType,
} from '../../components/Popups/ReportSummaryPopup';
import { SelectPeriodPopup } from '../../components/Popups/SelectPeriodPopup';
import { PerformanceReportTemplateV2 } from '../../components/Templates';
import { usePopup } from '../../context';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import useRedirectHome from '../../hooks/useRedirectHome';
import { WebBoosterType } from '../../models/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore, useSpaceStoreListField } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { getPeriod, PeriodType } from '../../utils/period';

type State = {
  selectedPeriod: PeriodType;
  periodRange: string;
  from: Date | undefined;
  to: Date | undefined;
  selectedBoosterType: WebBoosterType | undefined;
  partitionId: string;
  performanceData: SpacePerformanceReportSummary | undefined;
  isLoading: boolean;
  isFinishedFetchSpace: boolean;
};

const { fetchSpace, getPerformanceReport } = useSpaceStore.getState();

export const PerformanceReportScreenV2: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const { showPopup, hidePopup } = usePopup();

  const { classroomId } = useParams();
  const navigationState = location.state as {
    selectedPeriod: PeriodType;
  } | null;

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const partitionId = useSessionStore((store) => store.partitionId);

  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );
  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );

  const periodType = useMemo(() => {
    if (navigationState?.selectedPeriod) {
      return navigationState?.selectedPeriod;
    }
    if (window.history.state) {
      const windowState = window.history.state as
        | { selectedPeriod: PeriodType }
        | undefined;
      if (windowState?.selectedPeriod) {
        return windowState?.selectedPeriod;
      }
    }
    return PeriodType.Today;
  }, [navigationState?.selectedPeriod]);

  const currentDate = useMemo(() => new Date(), []);
  const period = useMemo(() => {
    return getPeriod(currentDate, periodType, i18n.language);
  }, [currentDate, i18n.language, periodType]);

  const [state, setState] = useState<State>({
    selectedPeriod: period?.key ?? PeriodType.Today,
    periodRange: period?.range ?? '',
    from: period?.from,
    to: period?.to,
    selectedBoosterType: userAccount?.settings.webBoosterType,
    partitionId: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.All}`,
    performanceData: undefined,
    isLoading: true,
    isFinishedFetchSpace: false,
  });

  useEffect(() => {
    setState((s) => ({
      ...s,
      partitionId:
        partitionId ??
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`,
    }));
  }, [partitionId, state.selectedBoosterType]);

  useRedirectEntry();

  // MARK: Get Space
  const spaceListFieldName = `${classroomId}`;
  useSpaceStoreListField('spaces', spaceListFieldName);

  useEffect(() => {
    if (classroomId) {
      fetchSpace(spaceListFieldName, classroomId)
        .catch(console.error)
        .finally(() => {
          setState((s) => ({ ...s, isFinishedFetchSpace: true }));
        });
    }
  }, [classroomId, spaceListFieldName]);

  const classroom = useSpaceStore(
    (store) => store.getSpace(classroomId ?? ''),
    shallow,
  );

  useRedirectHome(state.isFinishedFetchSpace && !classroom);

  const breadcrumbs = useMemo(() => {
    const folders =
      classroom?.getAllPartitions(userAccount?.settings.webBoosterType) ?? [];
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.partitionId,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [userAccount?.settings.webBoosterType, classroom, state.partitionId]);

  // MARK: Fetch Performance Report
  const fetchSpacePerformanceReport = useCallback(() => {
    setState((s) => ({ ...s, isLoading: true }));
    getPerformanceReport({
      classroomId: classroomId ?? '',
      webBoosterType: state.selectedBoosterType,
      partitionId:
        state.partitionId ===
        `${state.selectedBoosterType}-${FolderBreadcrumbText.All}`
          ? undefined
          : state.partitionId,
      from: state.from?.getTime().toString(),
      to: state.to?.getTime().toString(),
    })
      .then((summary) => {
        if (summary) {
          setState((s) => ({
            ...s,
            performanceData: summary.performanceReport,
          }));
        }
      })
      .finally(() => {
        setState((s) => ({ ...s, isLoading: false }));
      })
      .catch(console.error);
  }, [
    classroomId,
    state.from,
    state.partitionId,
    state.selectedBoosterType,
    state.to,
  ]);

  useEffect(() => {
    if (classroomId) {
      fetchSpacePerformanceReport();
    }
  }, [classroomId, fetchSpacePerformanceReport]);

  const msToTime = useCallback((ms: number): string => {
    const seconds = Math.floor((ms / 1000) % 60);
    const minutes = Math.floor((ms / (1000 * 60)) % 60);
    const hours = Math.floor(ms / (1000 * 60 * 60));

    const parts: string[] = [];

    if (hours > 0) {
      parts.push(`${hours}hr`);
    }
    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }
    if (seconds > 0 || parts.length === 0) {
      parts.push(`${seconds}s`);
    }

    return parts.join(' ');
  }, []);

  const { avgGameScore, completedGames, timeSpent, lastModifiedAt, analysis } =
    useMemo(() => {
      if (state.performanceData) {
        const completedGameCount = state.performanceData.completedGameCount;
        const scoreTotal = state.performanceData.scoreTotal;
        // const totalDurationInMs = getTimeString(
        //   state.performanceData.totalDurationInMs,
        //   'HH:mm:ss',
        //   false,
        // );
        const totalDurationInMs = msToTime(
          state.performanceData.totalDurationInMs,
        );

        const _lastModifiedAt = state.performanceData.lastModifiedAt;
        const performances = state.performanceData.performances;
        return {
          avgGameScore: parseFloat(
            numbro(
              completedGameCount > 0 ? scoreTotal / completedGameCount : 0,
            ).format({
              mantissa: 2,
              trimMantissa: true,
              optionalMantissa: true,
            }),
          ),
          completedGames: completedGameCount,
          timeSpent: totalDurationInMs,
          lastModifiedAt: new Date(_lastModifiedAt),
          analysis: performances,
        };
      }
      return {
        avgGameScore: 0,
        completedGames: 0,
        timeSpent: '',
        lastModifiedAt: new Date(),
        analysis: {},
      };
    }, [msToTime, state.performanceData]);

  const navigateGameList = useCallback(() => {
    if (classroomId === activeUserId) {
      navigate('/club/journey');
    } else {
      navigate(`/club/${classroomId}`);
    }
    hidePopup();
  }, [activeUserId, classroomId, hidePopup, navigate]);

  const onClickSummary = useCallback(
    (summaryType: SummaryType) => {
      let value: string | number = 0;
      switch (summaryType) {
        case SummaryType.GamePlayed:
          value = completedGames;
          break;
        case SummaryType.GameScore:
          value = avgGameScore;
          break;
        case SummaryType.TimePlayed:
          value = timeSpent;
          break;
      }
      showPopup(
        <ReportSummaryPopup
          summaryType={summaryType}
          value={value}
          onPressPlay={navigateGameList}
          onPressCancel={hidePopup}
        />,
      );
    },
    [
      avgGameScore,
      completedGames,
      hidePopup,
      navigateGameList,
      showPopup,
      timeSpent,
    ],
  );

  const onClickPeriod = useCallback(() => {
    showPopup(
      <SelectPeriodPopup
        selectPeriod={state.selectedPeriod}
        createdAt={state.performanceData?.createdAt}
        onPrimary={hidePopup}
        onPeriod={(selected, range, from, to) => {
          setState((s) => ({
            ...s,
            selectedPeriod: selected,
            periodRange: range,
            from: from,
            to: to,
          }));
          window.history.replaceState({ selectedPeriod: selected }, '');
          hidePopup();
        }}
      />,
    );
  }, [
    hidePopup,
    showPopup,
    state.performanceData?.createdAt,
    state.selectedPeriod,
  ]);

  const onClickBreadcrumb = useCallback(
    (folder: string) => {
      if (classroom) {
        showPopup(
          <ClubSelectFolderPopup
            webBoosterType={userAccount?.settings.webBoosterType}
            space={classroom}
            selectedFolderId={folder}
            folders={classroom.getAllPartitions(
              userAccount?.settings.webBoosterType,
            )}
            newFolder={undefined}
            onClickSelect={onClickBreadcrumb}
            onCancel={hidePopup}
            onConfirm={() => {
              useSessionStore.getState().setPartitionId(folder);
              hidePopup();
            }}
          />,
        );
      }
    },
    [userAccount?.settings.webBoosterType, classroom, hidePopup, showPopup],
  );

  const onClickAnalysisLevel = useCallback(
    (performance: number) => {
      navigate(
        `/club/${classroomId}/performance-report/games?performance=${performance}&period=${state.selectedPeriod}`,
        { state: { withBackState: true } },
      );
    },
    [classroomId, navigate, state.selectedPeriod],
  );

  return (
    <PerformanceReportTemplateV2
      userProfile={userProfile}
      lastModifiedAt={lastModifiedAt}
      selectedPeriod={
        getPeriod(currentDate, state.selectedPeriod, i18n.language)?.name ?? ''
      }
      periodRange={state.periodRange}
      webBoosterType={state.selectedBoosterType}
      breadcrumbs={breadcrumbs}
      summary={{
        [SummaryType.GamePlayed]: completedGames,
        [SummaryType.GameScore]: avgGameScore,
        [SummaryType.TimePlayed]: timeSpent,
      }}
      analysis={analysis}
      isLoading={state.isLoading}
      onClickPeriod={onClickPeriod}
      onClickBreadcrumb={() => {
        onClickBreadcrumb(state.partitionId);
      }}
      onClickSummary={onClickSummary}
      onClickAnalysisLevel={onClickAnalysisLevel}
    />
  );
};

export default PerformanceReportScreenV2;
